import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Log } from 'app/core/logging/log';
import { LocationAvailableModel } from 'app/models/location-available-model';
import { RestService } from 'app/services/rest/rest.service';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'app-location-selector',
  templateUrl: './location-selector.component.html',
  styleUrls: ['./location-selector.component.scss']
})
export class LocationSelectorComponent implements OnInit, OnChanges, OnDestroy {

  @Input() selected: LocationAvailableModel | null = null;
  @Output() selectedChange = new EventEmitter<{location: LocationAvailableModel, autoSelected:boolean}>();

  locations: LocationAvailableModel[] = [];

  isGlobalView = false;
  isDisabled = false;
  isSelectionRequired = false;

  private sub!: Subscription;

  constructor(
    private restService: RestService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.listenRouteChanges();
    this.evaluateRoute();
    this.loadLocations();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selected']) {
      this.syncSelectedWithLocations();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get isGlobalMode(): boolean {
    return this.isGlobalView && this.locations.length > 1 && !this.isSelectionRequired;
  }

  loadLocations() {
    this.restService.findClientLocker().subscribe((result: LocationAvailableModel[]) => {
      this.locations = result;
      this.syncSelectedWithLocations();

      // Auto-seleccion si solo hay uno
      if (this.locations.length === 1) {
        this.select(this.locations[0], true);
      }

      this.evaluateState();
    });
  }

  select(location: LocationAvailableModel, autoSelected:boolean) {
    this.selected = location;
    this.selectedChange.emit({location: location, autoSelected: autoSelected});
  }

  listenRouteChanges() {
    this.sub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.evaluateRoute();
        this.evaluateState();

        Log.debug('url', { event });
      });
  }

  evaluateState() {
    const multipleLocations = this.locations.length > 1;

    // En vistas globales normales se bloquea el selector.
    // Si hemos llegado porque falta location, debe quedar habilitado.
    if (this.isGlobalView && multipleLocations && !this.isSelectionRequired) {
      this.isDisabled = true;
      return;
    }

    this.isDisabled = false;
  }

  evaluateRoute() {
    let route = this.router.routerState.snapshot.root;
    const queryParams = this.router.routerState.snapshot.root.queryParams || {};

    while (route.firstChild) {
      route = route.firstChild;
    }

    const allow = route.data?.['allowWithoutLocation'];

    this.isSelectionRequired = queryParams['reason'] === 'no-location';
    this.isGlobalView = allow === true;

    if (this.isGlobalView && !this.selected) {
      this.selected = null;
    }

    Log.debug('Ruta activa:', {
      path: route.routeConfig?.path,
      data: route.data,
      allow: allow,
      isGlobalView: this.isGlobalView,
      isSelectionRequired: this.isSelectionRequired
    });
  }

  private syncSelectedWithLocations(): void {
    if (!this.selected || !this.locations.length) {
      return;
    }

    const matchingLocation = this.locations.find(
      (location) => location.locationsId === this.selected?.locationsId
    );

    if (matchingLocation) {
      this.selected = matchingLocation;
    }
  }

  byClientId = (a: LocationAvailableModel | null, b: LocationAvailableModel | null) =>
    a && b ? a.locationsId === b.locationsId : a === b;
}
